import { useMemo } from "react"

import { useMetricsListRecordDataTransport } from "@/metrics/transport-data"

import {
  buildMetricRecordInstancesById,
  buildMetricRecordsByKey,
} from "../utils/metricRecordLookups"

interface LookupHookOptions {
  enabled?: boolean
  preserveReferencedIds?: boolean
  referencedMetricIds?: string[]
  referenceRows?: Array<{
    metric_outputs?: Record<string, unknown>
    metric_values?: Record<string, unknown> | null
    metric_averages?: Record<string, unknown>
    metric_ids?: string[]
  }>
}

function useReferencedMetricIds(options?: LookupHookOptions) {
  return useMemo(
    () => [
      ...new Set([
        ...(options?.referencedMetricIds ?? []),
        ...(options?.referenceRows ?? []).flatMap((row) => [
          ...Object.keys(row.metric_outputs ?? {}),
          ...Object.keys(row.metric_averages ?? {}),
          ...Object.keys(row.metric_values ?? {}),
          ...(row.metric_ids ?? []),
        ]),
      ]),
    ],
    [options?.referencedMetricIds, options?.referenceRows],
  )
}

export const useMetricRecords = (options?: LookupHookOptions) => {
  const referencedMetricIds = useReferencedMetricIds(options)
  const {
    data: rawMetricData,
    metricIdAliases,
    isLoading,
    isError,
    error,
    refetch,
  } = useMetricsListRecordDataTransport({
    enabled: options?.enabled,
    referencedMetricIds,
  })

  const metricRecords = useMemo(
    () => buildMetricRecordsByKey(rawMetricData),
    [rawMetricData],
  )

  return {
    metricRecords,
    metricIdAliases,
    isLoading,
    isError,
    error,
    refetch,
  }
}

export const useMetricRecordsById = (options?: LookupHookOptions) => {
  const referencedMetricIds = useReferencedMetricIds(options)
  const {
    data: rawMetricData,
    metricIdAliases,
    isLoading,
    isError,
    error,
    refetch,
  } = useMetricsListRecordDataTransport({
    enabled: options?.enabled,
    referencedMetricIds,
  })

  const metricRecordsById = useMemo(
    () =>
      buildMetricRecordInstancesById(
        rawMetricData,
        options?.preserveReferencedIds ? metricIdAliases : undefined,
      ),
    [rawMetricData, metricIdAliases, options?.preserveReferencedIds],
  )

  return {
    metricRecordsById,
    metricIdAliases,
    isLoading,
    isError,
    error,
    refetch,
  }
}
